import { XMarkIcon } from '@heroicons/react/24/solid';
import { ChatBubbleLeftEllipsisIcon, PhoneIcon } from '@heroicons/react/20/solid';
import {
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  ChatBubbleBottomCenterTextIcon,
  CheckBadgeIcon,
  DocumentCheckIcon,
  EllipsisHorizontalIcon,
  FlagIcon,
  NoSymbolIcon,
  ShareIcon,
  StarIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';

import { MouseEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import {
  CvDetailAvailableOperations,
  CvDetailAvailableOperationsCurrentStatus,
  CvDetailObject,
  CvDetailRegisteredActions,
} from '@components/templates/cv/interface/CvDetailInterface';
import { Button, Modal, Tooltip } from '@components/common';
import { useBjModal } from '@hooks/common/useBjModal';
import { ShareModal } from '@components/features/modals/share-modal/ShareModal';
import { CustomConfirmModalContent } from '@components/templates/cv/partials/modals/CustomConfirmModalContent';
import { useTranslation } from 'next-i18next';
import { useStaticToasts } from '@hooks/useStaticToasts/useStaticToasts';
import { SendMessageModalContent } from '@components/templates/cv/partials/modals/SendMessageModalContent';
import { ResumeArrowsNavigation } from '@components/templates/cv/partials/navigation/ResumeArrowsNavigation';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { ApiCheckUnlock, fetcher, isApiV1Errors, isCheckUnlockStatusOk, ResponseWithHeaders } from '@utils/data-fetching';
import useWindowDimensions from '@hooks/useMediaQuery';
import { useAppDispatch, useAppSelector } from 'src/app';
import { AddressBookSolidIcon } from '@components/common/Icons';
import { useApplicantListsRedux } from '@components/templates/employer/cards-lists/hooks/useApplicantListsRedux';
import { useSwrAvailableResumeOperations } from '@hooks/useSwrApi/endpoints/user';
import { filterAndOrderAvailableOperations } from '@components/templates/cv/utils';
import { AddCommentModalContent } from '@components/templates/cv/partials/modals/AddCommentModalContent';
import { AiEvaluationFeedbackModal } from '@components/features/modals/ai-evaluation-feedback/AiEvaluationFeedbackModal';
import { getCommentUnlockResponse } from '@store/reducers/employer/resume/resumeSelector';
import { clearCommentUnlockResponse } from '@store/reducers/employer/resume/resumeReducer';
import { EmployerResumeUnlockResponse } from '@type/redux';
import { useSwrResumeCheckUnlockPrice } from '@hooks/useSwrApi/endpoints/resume-operations/useSwrResumeCheckUnlockPrice';
import { showAddCompanyInfoModal } from '@components/features/modals/employer/AddCompanyInfoModal';
import { showDownloadPdfEvaluationModal } from '@components/features/modals/cv/DownloadPdfEvaluationModal';
import { showRejectionReasonModal } from '@components/features/modals/employer/rejection-reason/RejectionReasonModal';
import { trackEvent } from '@services/tracking';
import { useAuthClient } from '@root/src/hooks';
import { useUnblockOperations } from '@components/templates/cv/hooks/useUnblockOperations';
import { InterfaceJobFolderOptions } from '@components/templates/cv/sections/ProfileHeading';
import { RecruiterCallSoftphoneModal } from '@components/features/recruiter-call';
import { useRecruiterCallEntry } from '@components/features/recruiter-call/useRecruiterCallEntry';
import { StatusChangeActorEnum } from '@type/v1-api-types';
import { CvActionButton, CvActionMenuItem } from '@components/templates/cv/sections/operations/partials';
import { getStatusSourceLabelKey } from '@utils/resume/status-source';

// Forward pipeline steps, in order: Shortlist -> Finalist -> Hired. The backend
// (APPLICANT_STATUS_MAP) only returns the operations allowed from the current status.
// Offer (23) is no longer offered; candidates still on it count as Finalist.
const STATUS_FLOW = ['21', '3', '22'];

const STATUS_TEXT_CLASSES: Record<string, string> = {
  1: 'text-ink-medium',
  2: 'text-ink-medium',
  21: 'text-primary',
  3: 'text-pink',
  23: 'text-orange-600',
  22: 'text-secondary-dark',
  4: 'text-error',
};

const STATUS_ICONS: Record<string, ReactNode> = {
  21: <StarIcon />,
  3: <TrophyIcon />,
  23: <DocumentCheckIcon />,
  22: <CheckBadgeIcon />,
};

export const UnblockActions = ({
  cvDetail,
  reFetchData,
  jobFolderId,
  isSinglePage,
  jobTitle,
  jobSlug,
  sendCurrentCvStatus,
  slug,
  setLoadingCvStatus,
  setTelephoneHighlight,
  setCurrentJobFolderHighlight,
  isFromJobContext,
  applicantSeenContact,
  applicantSeenContactStatus,
  closeCvModal,
  isCompany,
  rid,
  withNavbarInModal,
  locale,
  statusChangeFromModal,
  setJobFolderInfo,
  hasSourcingPackage = false,
  actionsContainer,
  onRegisterActions,
  swrTalentList,
  openTalentModal,
  showLoadMore,
  size,
  setSize,
}: {
  cvDetail: CvDetailObject;
  setLoadingCvStatus: () => void;
  setJobFolderInfo: (jobFolderInfo: InterfaceJobFolderOptions) => void;
  statusChangeFromModal?: (statusId: string, slug: string) => void;
  setTelephoneHighlight: () => void;
  setCurrentJobFolderHighlight: () => void;
  jobSlug: string;
  jobTitle: string;
  isSinglePage?: boolean;
  slug: string;
  jobFolderId: number;
  isFromJobContext: boolean;
  applicantSeenContactStatus: boolean;
  reFetchData: (onSuccessCallback?: (cv: CvDetailObject) => void) => void;
  applicantSeenContact: () => void;
  sendCurrentCvStatus: (item: CvDetailAvailableOperationsCurrentStatus) => void;
  closeCvModal?: () => void;
  isCompany: boolean;
  rid?: string;
  withNavbarInModal?: boolean;
  locale: Locale;
  hasSourcingPackage?: boolean;
  /** Element in ProfileHeading where the CV actions are portaled to. */
  actionsContainer?: HTMLElement | null;
  onRegisterActions?: (actions: CvDetailRegisteredActions) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  swrTalentList?: any[];
  openTalentModal?: (slug: string, as?: string) => void;
  showLoadMore?: boolean;
  size?: number;
  setSize?: (size: number) => void;
}) => {
  const { t } = useTranslation('common');
  const userSlug = cvDetail.slug;
  const userIdToUnlock = cvDetail?.id as number;
  const [sendMessageModal, setSendMessageModal] = useState<boolean>(false);
  const [unlockedByCall, setUnlockedByCall] = useState<boolean>(false);
  const [currentCVStatus, setCurrentCVStatus] = useState<CvDetailAvailableOperationsCurrentStatus>();
  const [currentJobSlug, setCurrentJobSlug] = useState<string>('');
  const telRef = useRef<HTMLAnchorElement>(null);
  const { status200Toast, showStatusToast, statusGenericErrorToast } = useStaticToasts();
  const { isMobile } = useWindowDimensions();
  const headerInfo = useAppSelector((state) => state.user.headerInfo);
  const userId = headerInfo?.userId;
  const [isLoadingStatusChange, setIsLoadingStatusChange] = useState<boolean>(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [isReportEvaluationModalOpen, setIsReportEvaluationModalOpen] = useState(false);

  const { requestUnlockWithConfirm, confirmModalOpen, confirmUnlock, cancelUnlock } = useUnblockOperations({
    rid,
    userIdToUnlock,
    isCompany,
    jobSlug,
    jobTitle,
    jobFolderId,
    locale,
    userId,
    slug,
    ...(jobSlug && { jobSlug }),
    // Freemium applicant: the unlock options open the job upgrade dialog rather
    // than the pricing confirmation, since there is no per-candidate price to
    // confirm. `reFetchData` is a prop, so it is safe to reference here — the
    // other refresh helpers are declared further down.
    contactRequiresJobUpgrade: cvDetail?.contactRequiresJobUpgrade,
    onJobUpgraded: () => reFetchData(),
  });

  // Redux states for checking external operations response
  const commentUnlockResponse = useAppSelector(getCommentUnlockResponse);
  const dispatch = useAppDispatch();

  const { isAuth, redirectToLogin } = useAuthClient();

  // Redux states for infinite lists. When in modal, with this we watch if:
  // - The applicant/talent status has changed (interview, shortlist, etc.).
  // - The CV was previously locked but now has been unlocked via message, show contact details, download, etc.
  // We will mutate the infinite list according to these states.
  // *************************************
  const [statusChanged, setStatusChanged] = useState<boolean>(false);
  const [lockedChanged, setLockedChanged] = useState<boolean>(false);
  const [isFreeCvInfoUnblock, setIsFreeCvInfoUnblock] = useState<boolean>(false);
  const [newStatusId, setNewStatusId] = useState<number | undefined>();
  useApplicantListsRedux({
    statusChanged,
    lockedChanged,
    slug,
    statusId: newStatusId,
  });

  // SWR mutation: Unlock for Message
  // ******************************************************
  const { trigger: triggerCheckUnlockPrice } = useSwrResumeCheckUnlockPrice({
    userSlug,
    locale,
    successCallback: (response) => {
      // If the response is false, the contact cost is 10 neuro
      setIsFreeCvInfoUnblock(response.is_unlock);
    },
  });

  // Handle share modal
  // *************************************
  const { isOpen: isShareModalOpen, toggleModal: toggleShareModal } = useBjModal();

  // SWR: Get available operations
  const { data: availableResumeOperations, mutate: refetchResumeOperations } = useSwrAvailableResumeOperations({
    locale,
    slug,
    shouldFetch: isAuth && isCompany,
    queryParams: {
      jobSlug: jobSlug?.length ? jobSlug : currentJobSlug,
      rid,
    },
    onSuccess: (response) => {
      setCurrentCVStatus(response?.currentStatus as CvDetailAvailableOperationsCurrentStatus);
      sendCurrentCvStatus(response?.currentStatus as CvDetailAvailableOperationsCurrentStatus);
    },
  });

  const downloadEndpoint = availableResumeOperations?.availableOperations.find((op) => op.operationId === 'download')?.operationEndpoint;

  // List of available operations filtered and ordered
  const availableOperations = useMemo(
    () => filterAndOrderAvailableOperations(availableResumeOperations?.availableOperations ?? []),
    [availableResumeOperations?.availableOperations],
  );

  const handleSuccessStatusChanged = useCallback(
    (response: EmployerResumeUnlockResponse) => {
      setCurrentJobSlug(response.response.jobSlug);
      // If the response status is success we need to re-fetch the available operations
      void refetchResumeOperations();
      sendGoogleTagEvent(`employer_cv_set_${response.operationName?.toLowerCase()}`, 'Custom');
      setIsLoadingStatusChange(false);
      setStatusChanged(true);
      setNewStatusId(response.statusId);
    },
    [refetchResumeOperations],
  );

  useEffect(() => {
    if (commentUnlockResponse) {
      dispatch(clearCommentUnlockResponse());
      handleSuccessStatusChanged(commentUnlockResponse);
    }
  }, [commentUnlockResponse, dispatch, handleSuccessStatusChanged]);

  useEffect(() => {
    // /check-unlock requires auth — skip for guests (401) and keep the
    // default locked/unlock buttons (isFreeCvInfoUnblock stays false).
    if (!isAuth) return;
    void triggerCheckUnlockPrice({ jobId: jobFolderId, title: jobTitle, jobSlug });
  }, [isAuth, triggerCheckUnlockPrice, jobFolderId, jobTitle, jobSlug]);

  const sendContactedData = useCallback(() => {
    fetcher(`/v1/resume-operation/${userIdToUnlock}/contact${rid ? `?rid=${rid}` : ''}`, locale, {
      method: 'POST',
      payload: {
        type: 'contact',
        jobSlug,
      },
      stringifyPayload: true,
    })
      .then((response) => {
        if (isApiV1Errors(response)) {
          const { code } = response.errors[0];
          showStatusToast(String(`${code}`));
        } else if (!applicantSeenContactStatus) {
          applicantSeenContact();
          setTelephoneHighlight();
        }
      })
      .catch(() => {});
  }, [userIdToUnlock, rid, locale, jobSlug, showStatusToast, applicantSeenContactStatus, applicantSeenContact, setTelephoneHighlight]);

  const isPromoPrice = !!(cvDetail?.fullPrice && cvDetail?.price && cvDetail?.price < cvDetail?.fullPrice);
  // Freemium applicant: contacting goes through a paid job upgrade, so nothing
  // in this component should quote or confirm a per-candidate price.
  const requiresJobUpgrade = cvDetail?.contactRequiresJobUpgrade === true;

  // we use the fetcher version with headers, because we need to get the filename from the response headers
  const getDownloadedPDF = (includeEvaluation: boolean = true) => {
    const params = new URLSearchParams();
    if (rid) params.set('rid', rid);
    // API takes the inverse — `excludeEvaluation`, default false. The
    // modal is authored positively ("include AI evaluation") for UI
    // clarity; translate here.
    if (!includeEvaluation) params.set('excludeEvaluation', '1');
    const qs = params.toString();
    const apiURL = qs ? `${downloadEndpoint}?${qs}` : `${downloadEndpoint}`;
    fetcher<unknown>(apiURL, locale, {
      method: 'GET',
      responseType: 'blob',
      withResponseHeaders: true,
    })
      .then((res) => {
        // this is how you make the response headers available and let TS know about it (assert res as ResponseWithHeaders<T>)
        const { response, responseHeaders }: { response: BlobPart; responseHeaders: Headers } = res as ResponseWithHeaders<BlobPart>;
        const filename = responseHeaders.get('content-disposition')?.split('filename=')[1];
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || `${userSlug}.pdf`);
        document.body.appendChild(link);
        link.click();
        reFetchData();
        //   aici punem tracking event de download
        trackEvent({
          eventType: 'action',
          entityType: 'resume',
          entityId: slug,
          action: 'contact',
          viewContext: 'detail',
          ...(userId && { userId: userId.toString() }),
          ...(rid && { listUuid: rid }),
        });
      })
      .catch(() => {});
  };

  // Unlock with PDF if the confirmation modal is true
  const unlockWithDownloadPdf = (includeEvaluation: boolean = true) => {
    try {
      requestUnlockWithConfirm({
        type: 'download',
        onSuccess: (res) => {
          if (isCheckUnlockStatusOk(res)) {
            sendGoogleTagEvent('premium_contact_download', 'Custom');
            getDownloadedPDF(includeEvaluation);
            reFetchData();
            void refetchResumeOperations();
            setLockedChanged(true);
            if (res.meta?.consumptionInfoSummary) {
              status200Toast(res.meta.consumptionInfoSummary);
            }
          }
        },
      });
    } catch {
      console.warn('error');
    }
  };

  // Runs the actual download (unlocking first if needed) with the resolved
  // evaluation preference.
  const runPdfDownload = (includeEvaluation: boolean) => {
    if (cvDetail.locked === false) {
      getDownloadedPDF(includeEvaluation);
    } else {
      unlockWithDownloadPdf(includeEvaluation);
    }
  };

  // Entry point: prompt for the evaluation switcher only when the candidate
  // actually has one — otherwise the default (with evaluation) is meaningless
  // and the popup would just be noise.
  const checkUnlockPdf = () => {
    if (cvDetail.evaluation) {
      showDownloadPdfEvaluationModal({ onConfirm: runPdfDownload });
    } else {
      runPdfDownload(true);
    }
  };

  // Stable handlers for actions consumed outside UnblockActions (ProfileComments,
  // EvaluateBia). setState setters from useState already have stable identity,
  // so wrapping them in useCallback with [] gives us a stable reference for free.
  const handleAddComment = useCallback(() => setIsCommentModalOpen(true), []);
  const handleReportEvaluation = useCallback(() => setIsReportEvaluationModalOpen(true), []);

  // Memoize the registered actions so the object identity only changes when a
  // meaningful value does — otherwise the parent setter would run on every
  // render and trigger a re-render loop (see CvDetailHub#handleRegisterActions
  // which bails out on identical references).
  const registeredActions = useMemo<CvDetailRegisteredActions>(
    () => ({
      onAddComment: handleAddComment,
      onReportEvaluation: handleReportEvaluation,
      isFreeCvInfoUnblock,
    }),
    [handleAddComment, handleReportEvaluation, isFreeCvInfoUnblock],
  );

  useEffect(() => {
    onRegisterActions?.(registeredActions);
  }, [onRegisterActions, registeredActions]);

  const unlockWithMessage = () => {
    if (cvDetail.locked === false) {
      setSendMessageModal(true);
    } else {
      try {
        requestUnlockWithConfirm({
          type: 'message',
          onSuccess: (res) => {
            if (isCheckUnlockStatusOk(res)) {
              setCurrentJobFolderHighlight();
              setSendMessageModal(true);
              reFetchData();
              void refetchResumeOperations();
              sendGoogleTagEvent('premium_contact_message', 'Custom');
              setLockedChanged(true);
              if (res.meta?.consumptionInfoSummary) {
                status200Toast(res.meta.consumptionInfoSummary);
              }
            }
          },
        });
      } catch {
        console.warn('error');
      }
    }
  };

  const unlockContactData = useCallback(
    (onSuccessCallback?: (cv: CvDetailObject) => void) => {
      if (cvDetail?.privateInformation?.phone !== undefined && unlockedByCall) {
        setUnlockedByCall(false);
        if (onSuccessCallback) onSuccessCallback(cvDetail);
      } else {
        try {
          requestUnlockWithConfirm({
            type: 'contact',
            onSuccess: (res) => {
              if (isCheckUnlockStatusOk(res)) {
                sendContactedData();
                setCurrentJobFolderHighlight();
                setUnlockedByCall(true);
                reFetchData(onSuccessCallback);
                void refetchResumeOperations();
                sendGoogleTagEvent('premium_contact_call', 'Custom');
                setTimeout(() => {
                  telRef?.current?.click();
                }, 750);
                setLockedChanged(true);
                if (res.meta?.consumptionInfoSummary) {
                  status200Toast(res.meta?.consumptionInfoSummary);
                }
              }
            },
          });
        } catch {
          console.warn('error');
        }
      }
    },
    [
      cvDetail,
      unlockedByCall,
      requestUnlockWithConfirm,
      sendContactedData,
      setCurrentJobFolderHighlight,
      reFetchData,
      refetchResumeOperations,
      status200Toast,
    ],
  );

  // Change status to specific operation, each item has the parameters in the available
  // action we need to get them out
  const applyStatusChange = useCallback(
    (params: CvDetailAvailableOperations) => {
      setIsLoadingStatusChange(true);
      const eventName = (params?.name ?? '').toLowerCase();
      const uId = params.operationParams.filter((x: { id: string }) => x.id === 'userId').map((item: { value: number }) => item.value)[0];
      const jobId = params.operationParams.filter((x: { id: string }) => x.id === 'jobId').map((item: { value: number }) => item.value)[0];
      const statusId = params.operationParams.filter((x: { id: string }) => x.id === 'status').map((item: { value: number }) => item.value)[0];

      // In case of CV view, if we have dropdown for job select we need to overwrite the job context,
      // so that the operation will be applied to job context
      const jobFinalId = jobId === undefined ? jobFolderId : jobId;

      fetcher<ApiCheckUnlock>(`${params.operationEndpoint}${rid ? `?rid=${rid}` : ''}`, locale, {
        method: 'POST',
        payload: {
          userId: uId,
          status: statusId,
          jobId: jobFinalId,
          // Same job the status was loaded for. The backend falls back to it when jobId is missing (0), e.g. for the
          // operations the dropdown builds itself; without it the status lands on the recruiter's last job instead
          jobSlug: jobSlug?.length ? jobSlug : currentJobSlug,
          title: jobTitle,
        },
        stringifyPayload: true,
      })
        .then((response) => {
          if (isCheckUnlockStatusOk(response)) {
            // aplicam tracking pentru status change
            if (statusId === 3) {
              trackEvent({
                eventType: 'action',
                entityType: 'resume',
                entityId: slug,
                action: 'interview',
                viewContext: 'detail',
                ...(userId && { userId: userId.toString() }),
                ...(rid && { listUuid: rid }),
              });
            }

            handleSuccessStatusChanged({
              response,
              operationName: eventName,
              statusId,
            });

            // Show rejection reason modal when rejecting a recommended applicant
            if (Number(statusId) === 4) {
              const matchingScore = cvDetail?.application?.evaluation?.score;
              const applicantId = cvDetail?.application?.applicantId;
              if (applicantId) {
                showRejectionReasonModal(locale, applicantId, matchingScore);
              }
            }

            statusChangeFromModal?.(statusId.toString(), slug);
          } else if (isApiV1Errors(response)) {
            const { code } = response.errors[0];
            if (code === 403) {
              const successCallback = () => applyStatusChange(params);
              showAddCompanyInfoModal(locale, successCallback);
              setIsLoadingStatusChange(false);
              return;
            }
            showStatusToast(String(`${code}`));
            setIsLoadingStatusChange(false);
          } else {
            // Anything else: unknown response shape. Clear the spinner so
            // the buttons don't stay stuck; a diagnostic hint helps if this
            // ever escapes production silently.
            setIsLoadingStatusChange(false);
          }
        })
        .catch(() => {
          // Fetch layer error (network, abort) — never let the spinner stick.
          setIsLoadingStatusChange(false);
        });
    },
    [jobFolderId, rid, locale, jobTitle, jobSlug, currentJobSlug, userSlug, showStatusToast, statusGenericErrorToast, t, handleSuccessStatusChanged, statusChangeFromModal, slug, userId, cvDetail?.application, reFetchData, refetchResumeOperations],
  );

  // We need to check if the phone was unlocked by request, or it can make already call without
  // checking unlock
  useEffect(() => {
    if (cvDetail?.privateInformation?.phone !== undefined && unlockedByCall) {
      setUnlockedByCall(false);
    }
  }, [unlockedByCall, cvDetail]);

  const setLoadingCvStatusFromOperation = () => {
    setLoadingCvStatus();
  };

  const triggerPhoneLoad = useCallback(
    (event: MouseEvent) => {
      if (!isMobile) {
        event.preventDefault();
      }
      setTelephoneHighlight();
    },
    [isMobile, setTelephoneHighlight],
  );

  // success data unlocked — OpenTok video-call hook removed in this PR;
  // the post-unlock branch that started a call is now a no-op. Generic
  // typed so callers (`onViewContactDataPressed`) keep passing `(cv,
  // withCall)` without TS arity errors and a future video-call hook
  // can drop in by replacing the body. Empty body intentional; no
  // unused-vars violation because the signature lives on the type
  // parameter, not on the implementation.
  const onSuccessContactDataUnlocked = useCallback<(cv: CvDetailObject, withCall?: boolean) => void>(() => {}, []);

  // Unlock contact data and show the phone number.
  // Show call options based on the `withCall` parameter
  const onViewContactDataPressed = useCallback(
    (event: MouseEvent | null, withCall = false, onSuccess?: (cv: CvDetailObject) => void) => {
      // Check if the user has a phone number, if not we need to unlock it
      if (cvDetail?.privateInformation?.phone === undefined && !unlockedByCall) {
        unlockContactData((cv) => {
          onSuccessContactDataUnlocked(cv, withCall);
          if (onSuccess) onSuccess(cv);
        });
        return;
      }
      if (!applicantSeenContactStatus) {
        sendContactedData();
        if (onSuccess) onSuccess(cvDetail);
        return;
      }
      if (event) {
        triggerPhoneLoad(event);
      }
      onSuccessContactDataUnlocked(cvDetail, withCall);
      if (onSuccess) onSuccess(cvDetail);
    },
    [cvDetail, unlockedByCall, unlockContactData, onSuccessContactDataUnlocked, applicantSeenContactStatus, sendContactedData, triggerPhoneLoad],
  );


  // Header actions: Status
  // *****************************************************************************************************
  // Main part = next pipeline step the backend allows; dropdown = every step + reject, each one a toggle:
  // picking a status sets it, picking the current one unselects it (back to viewed).
  const statusOperationsById = useMemo(
    () => new Map((availableOperations ?? []).map((operation) => [operation.operationId, operation])),
    [availableOperations],
  );

  const statusAction = useMemo(() => {
    // Keep the button when the candidate has a status but no allowed change (e.g. hired): it then just shows the status
    if (!isCompany || (statusOperationsById.size === 0 && !currentCVStatus?.id)) return null;

    const currentId = currentCVStatus?.id;
    const getOperation = (statusId: string) => statusOperationsById.get(`status_${statusId}`);
    // The backend only lists the next steps (APPLICANT_STATUS_MAP), but the set-status endpoint accepts any status,
    // so the dropdown builds the operation itself when it isn't listed
    const getMenuOperation = (statusId: string): CvDetailAvailableOperations => getOperation(statusId) ?? {
      active: false,
      name: t(`applicant.status.${statusId}`),
      type: 'status',
      operationId: `status_${statusId}`,
      operationLink: 'openapi_set_cv_status',
      operationMethod: 'post',
      operationEndpoint: `/v1/resume-operation/${userIdToUnlock}/status/${statusId}`,
      operationParams: [{ id: 'userId', value: String(userIdToUnlock) }, { id: 'status', value: statusId }] as unknown as [],
    };
    const toggleStatus = (statusId: string) => applyStatusChange(getMenuOperation(statusId === currentId ? '2' : statusId));
    // status_2 (back to viewed) is offered as "reconsider" for rejected and "revoke" for hired candidates
    const revertOperation = currentId === '4' || currentId === '22' ? getOperation('2') : undefined;
    const revertLabel = currentId === '4' ? t('reconsider.candidate') : t('revoked.hired.candidate');

    const flowIndex = STATUS_FLOW.indexOf(currentId === '23' ? '3' : currentId ?? '');
    const nextStatusId = STATUS_FLOW[flowIndex + 1];
    const nextOperation = revertOperation ?? (nextStatusId ? getMenuOperation(nextStatusId) : undefined);

    const items: CvActionMenuItem[] = STATUS_FLOW.map((statusId) => ({
      id: statusId,
      label: t(`applicant.status.${statusId}`),
      icon: STATUS_ICONS[statusId],
      iconClassName: clsx('bg-surface-100', STATUS_TEXT_CLASSES[statusId]),
      current: statusId === currentId,
      onClick: () => toggleStatus(statusId),
    }));

    items.push({
      id: 'reject',
      label: t('reject.candidate'),
      icon: <NoSymbolIcon />,
      tone: 'danger',
      current: currentId === '4',
      separatorBefore: true,
      onClick: () => toggleStatus('4'),
    });

    const main = nextOperation
      ? {
        // Forward step reads "Move to / <status>"; reconsider / revoke are actions on their own
        eyebrow: revertOperation ? undefined : t('global.label.move.to'),
        label: revertOperation ? revertLabel : t(`applicant.status.${nextStatusId}`),
        icon: revertOperation ? <ArrowUturnLeftIcon className="size-4.5" /> : STATUS_ICONS[nextStatusId as string],
        className: revertOperation ? 'text-ink' : STATUS_TEXT_CLASSES[nextStatusId as string],
        onClick: () => applyStatusChange(nextOperation),
      }
      // Final step (e.g. hired): show the current status, nothing to advance to
      : {
        eyebrow: undefined,
        label: currentCVStatus?.name ?? '',
        icon: currentId ? STATUS_ICONS[currentId] : undefined,
        className: currentId ? STATUS_TEXT_CLASSES[currentId] : undefined,
        onClick: undefined,
      };

    return { items, main };
  }, [isCompany, statusOperationsById, currentCVStatus, t, applyStatusChange, userIdToUnlock]);

  // Who set the shortlist status (owner / admin / auto), shown next to the current status
  const statusActor = cvDetail?.application?.statusChangeActor;
  const isShortlisted = currentCVStatus?.id === '21';
  // Dot only when BestJobs moved the candidate (agent impersonating / Bestie automation); the account's own
  // recruiters (owner) are the normal case and get no dot
  const statusActorDotClass = ({
    [StatusChangeActorEnum.ADMIN]: 'bg-warning',
    [StatusChangeActorEnum.AUTO]: 'bg-secondary',
  } as Partial<Record<StatusChangeActorEnum, string>>)[statusActor as StatusChangeActorEnum];
  // Hover text for the dot, for every status (Shortlist keeps its own wording)
  const statusSourceLabelKey = getStatusSourceLabelKey(statusActor, isShortlisted);
  const statusSourceLabel = statusSourceLabelKey ? t(statusSourceLabelKey) : undefined;

  // No status = the candidate isn't in this job's pipeline yet (e.g. opened from talent search, in the context of the
  // recruiter's last job): show it as New, like the backend does for applicants without a status
  const displayedStatus = currentCVStatus?.name
    ? currentCVStatus
    : statusAction && { id: '1', name: t('applicant.status.1') };
  const currentStatusElement = displayedStatus ? (
    <span className="inline-flex items-center gap-1.5" title={statusSourceLabel}>
      {statusActorDotClass && <span className={clsx('size-1.5 shrink-0 rounded-full', statusActorDotClass)} />}
      <span className={clsx('font-bold', STATUS_TEXT_CLASSES[displayedStatus.id] ?? 'text-ink')}>{displayedStatus.name}</span>
    </span>
  ) : null;

  // Header actions: Contact
  // *****************************************************************************************************
  // Main part = call through the platform (when the quote allows it), otherwise see contact data.
  const callEntry = useRecruiterCallEntry({
    candidateUserId: userIdToUnlock,
    jobSlug: jobSlug ?? null,
    shouldFetch: isAuth && !cvDetail?.isOwnCV && !!userIdToUnlock,
  });
  const isFreeCall = callEntry.priceCredits === 0;
  let callBadge: string | undefined;
  if (!isFreeCall) {
    callBadge = `${callEntry.priceCredits} ${t('global.label.credits')}`;
  } else if (callEntry.remainingFreeCalls !== null) {
    callBadge = t('cv.detail.action.call.remaining_free_label', { count: callEntry.remainingFreeCalls, defaultValue: '{{count}} free calls left' });
  }

  // The price is suppressed for a freemium applicant: contacting goes through the job upgrade instead.
  const showContactPrice = (isFreeCvInfoUnblock || cvDetail?.locked) && !requiresJobUpgrade;
  const contactPriceElement = showContactPrice ? (
    <span className="font-semibold text-secondary-dark">
      {isFreeCvInfoUnblock && t('cv.detail.action.contact.free')}
      {!isFreeCvInfoUnblock && cvDetail?.locked === true && (hasSourcingPackage ? t('sourcing.contact.badge') : (
        <>
          {`${t('cv.detail.contact.label')}:`}
          {isPromoPrice && <span className="ml-1 text-danger line-through">{cvDetail?.fullPrice}</span>}
          <span className="ml-1">{`${cvDetail?.price} ${t('global.label.credits')}`}</span>
        </>
      ))}
    </span>
  ) : null;
  const contactButtonBadge = showContactPrice && !isFreeCvInfoUnblock && cvDetail?.locked === true && !hasSourcingPackage
    ? `${cvDetail?.price} ${t('global.label.credits')}`
    : undefined;

  const contactItems: CvActionMenuItem[] = [
    ...(callEntry.visible ? [{
      id: 'see-contact',
      label: t('cv.detail.action.see_contact.label'),
      icon: <AddressBookSolidIcon className="h-4 w-3.5 fill-current" />,
      onClick: (event: MouseEvent) => onViewContactDataPressed(event, true),
    }] : []),
    {
      id: 'message',
      label: t('cv.detail.action.message.label'),
      icon: <ChatBubbleLeftEllipsisIcon />,
      hasDot: cvDetail?.hasUnreadMessage,
      onClick: () => unlockWithMessage(),
    },
  ];

  const onlineDot = cvDetail?.online
    ? <span title={t('cv.detail.action.online.label')} className="block size-2 shrink-0 rounded-full bg-white ring-2 ring-white/40" />
    : null;

  // Header actions: More
  // *****************************************************************************************************
  // Main part = download CV (when allowed), otherwise the whole button opens the dropdown.
  const canDownload = isCompany && cvDetail?.id !== userId;
  const moreItems: CvActionMenuItem[] = [
    {
      id: 'share',
      label: t('cv.action.share'),
      icon: <ShareIcon />,
      onClick: toggleShareModal,
    },
    ...(isAuth && isCompany && !cvDetail?.isOwnCV ? [{
      id: 'comment',
      label: t('cv.action.comment.add'),
      icon: <ChatBubbleBottomCenterTextIcon />,
      onClick: handleAddComment,
    }] : []),
    ...(cvDetail?.application?.evaluation && cvDetail?.application?.applicantId ? [{
      id: 'report-evaluation',
      label: t('cv.action.report.evaluation'),
      icon: <FlagIcon />,
      tone: 'danger' as const,
      separatorBefore: true,
      onClick: handleReportEvaluation,
    }] : []),
  ];

  // Render main component
  // *****************************************************************************************************
  return (
    <>
      {/* Share modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={toggleShareModal}
        pathnameShareUrl={locale === 'ro' ? `/cv/${userSlug}` : `/${locale}/cv/${userSlug}`}
        ogTitle={t('cv.detail.meta.title')}
        ogDescription={`Bestjobs - ${t('cv.detail.meta.title')} - ${userSlug}`}
      />

      {isAuth && (
        <>
          <Modal
            size="sm"
            isOpen={confirmModalOpen}
            body={
              <CustomConfirmModalContent
                jobContextDetail={{
                  jobFolderId,
                  title: jobTitle,
                  jobSlug,
                }}
                updateActiveJobFolderContext={(item) => setJobFolderInfo(item)}
                userSlug={cvDetail?.slug}
                {...(cvDetail?.price !== undefined ? { defaultPrice: { price: Number(cvDetail.price), fullPrice: Number(cvDetail.fullPrice ?? 0) } } : {})}
                onSendSuccess={(val) => {
                  if (val) {
                    confirmUnlock();
                  } else {
                    cancelUnlock();
                  }
                }}
              />
            }
            onClose={() => null}
          />
          <Modal
            isOpen={sendMessageModal}
            body={
              <SendMessageModalContent
                imageUrl={cvDetail?.imgUrl ? cvDetail.imgUrl : ''}
                candidateName={cvDetail?.privateInformation?.name ? cvDetail.privateInformation.name : ''}
                onSendSuccess={() => setSendMessageModal(false)}
                onClose={() => {
                  setSendMessageModal(false);
                }}
                slug={slug}
                trackingId={rid}
                locale={locale}
              />
            }
            onClose={() => {
              setSendMessageModal(false);
            }}
          />

          {/* Add Comment Modal */}
          <Modal isOpen={isCommentModalOpen} onClose={() => setIsCommentModalOpen(false)} size="sm" centered>
            <Modal.Header onClose={() => setIsCommentModalOpen(false)}>
              <p className="text-lg text-ink">{t('cv.action.comment.add')}</p>
            </Modal.Header>
            <Modal.Body>
              <AddCommentModalContent cvUserId={userIdToUnlock} userSlug={slug} locale={locale} onClose={() => setIsCommentModalOpen(false)} />
            </Modal.Body>
          </Modal>

          {/* Report Evaluation Modal */}
          {cvDetail?.application?.evaluation && cvDetail?.application?.applicantId && (
            <AiEvaluationFeedbackModal
              isOpen={isReportEvaluationModalOpen}
              toggleModal={() => setIsReportEvaluationModalOpen(false)}
              identifier={cvDetail.application.applicantId.toString()}
              applicantEvaluation={cvDetail.application.evaluation}
            />
          )}
        </>
      )}

      {callEntry.isModalOpen && userIdToUnlock && (
        <RecruiterCallSoftphoneModal
          open={callEntry.isModalOpen}
          // The CV-detail DTO doesn't expose a public first name yet — only `privateInformation.name`,
          // which the backend nulls out for locked candidates; fall back to "Candidat" there.
          candidateFirstName={cvDetail?.privateInformation?.name ?? t('cv.detail.action.call.default_name', { defaultValue: 'Candidat' })}
          candidateFullName={cvDetail?.privateInformation?.name ?? null}
          candidateUserId={userIdToUnlock}
          jobSlug={jobSlug ?? null}
          priceCredits={callEntry.priceCredits}
          showOnboarding={!callEntry.recruiterHasHistory}
          onClose={callEntry.closeModal}
        />
      )}

      {/* Sticky navigation bar - only in modal: close + arrows to navigate between CVs */}
      <div
        className={clsx(
          'sticky z-20 self-start border-b border-surface-200 bg-surface/95 backdrop-blur-sm',
          // Standalone page: scroll container already sits below the fixed header,
          // stick at top-0. Otherwise (legacy single page where the body scrolls
          // under the fixed header, or a modal with an in-modal navbar) stick below it.
          withNavbarInModal || isSinglePage ? 'top-stickUnderHeader' : 'top-0',
          !closeCvModal && 'hidden',
        )}
      >
        <div className="flex w-full items-center px-4 py-2 lg:pl-8 lg:pr-16">
          <div className="flex items-center gap-1.5 empty:hidden md:gap-3">
            {closeCvModal && (
              <Tooltip tooltipText={t('global.btn.close')} position="bottom" colorScheme="default">
                <Button styling="none" onClick={closeCvModal} aria-label={t('global.btn.close')}>
                  <span className="flex size-8 items-center justify-center rounded-full bg-surface-100 hover:bg-surface-200">
                    <XMarkIcon className="size-5 text-ink" />
                  </span>
                </Button>
              </Tooltip>
            )}

            {/* Arrows to navigate between CVs. While we do not have the list in Next.js, the last search (filters) is stored */}
            {/* in a cookie, then the component gets the list of CVs for that search, together with the offset. */}
            <ResumeArrowsNavigation
              setLoadingCvStatusFromOperation={setLoadingCvStatusFromOperation}
              jobSlug={jobSlug}
              isFromJobContext={isFromJobContext}
              slug={slug}
              swrTalentList={swrTalentList}
              openTalentModal={openTalentModal}
              showLoadMore={showLoadMore}
              size={size}
              setSize={setSize}
            />
          </div>
        </div>
      </div>

      {/* CV actions (Status, Contact, More), rendered under the profile picture and details (ProfileHeading slot) */}
      {actionsContainer && createPortal(
        <div className="mb-2 mt-1">
          {/* Mobile info line: current status and call price (the desktop caption / call second line are hidden on
              mobile, where the Contact button often collapses to icon + arrow) */}
          {isAuth && !cvDetail?.isOwnCV && (currentStatusElement || (callEntry.visible && callBadge)) && (
            <div className="mb-2 flex items-center gap-2 text-[13px] text-ink-medium md:hidden">
              {currentStatusElement && (
                <span className="flex items-center gap-1">
                  {`${t('cv.action.more.section.status')}:`}
                  {currentStatusElement}
                </span>
              )}
              {callEntry.visible && callBadge && (
                <span className="ml-auto rounded-full bg-secondary/10 px-2 py-0.5 text-xs font-semibold text-secondary-dark">
                  {`${t('cv.detail.action.call.price_label')}: ${callBadge}`}
                </span>
              )}
            </div>
          )}
          <div
            className={clsx(
              'flex w-full items-center gap-2 md:gap-3',
              // Room for the status caption above the buttons
              isAuth && !cvDetail?.isOwnCV && statusAction && currentStatusElement && 'md:pt-6',
            )}
          >
            {/* Status — recruiters only, hidden on own CV */}
            {isAuth && !cvDetail?.isOwnCV && statusAction && (
              <CvActionButton
                variant="soft"
                className="md:min-w-44"
                caption={currentStatusElement && (
                  // flex keeps the label and the status (which starts with the actor dot) on one centre line
                  <span className="flex items-center gap-1">
                    {`${t('cv.action.more.section.status')}:`}
                    {currentStatusElement}
                  </span>
                )}
                eyebrow={statusAction.main.eyebrow}
                label={statusAction.main.label}
                icon={statusAction.main.icon}
                mainClassName={statusAction.main.className}
                onMainClick={statusAction.main.onClick ?? (() => {})}
                mainDisabled={!statusAction.main.onClick}
                isLoading={isLoadingStatusChange}
                menuLabel={t('cv.action.more.section.status')}
                menuHeader={currentStatusElement && (
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{t('cv.action.more.section.status')}</span>
                    {currentStatusElement}
                  </span>
                )}
                items={statusAction.items}
              />
            )}

            {/* Contact — call through the platform, or see contact data when calling isn't available */}
            {isAuth && !cvDetail?.isOwnCV && (
              <CvActionButton
                variant="primary"
                className="min-w-0 flex-1 md:flex-none"
                collapseLabel
                label={callEntry.visible
                  ? t(isFreeCall ? 'cv.detail.action.free-call.label' : 'cv.detail.action.call.label')
                  : t('cv.detail.action.see_contact.label')}
                icon={(
                  <>
                    {onlineDot}
                    {callEntry.visible ? <PhoneIcon className="size-4" /> : <AddressBookSolidIcon className="h-4 w-3.5 fill-current" />}
                  </>
                )}
                // Call price / free calls on the second line (desktop; mobile shows it in the info line above);
                // the contact price stays a badge
                subLabel={callEntry.visible && callBadge ? <span className="hidden md:inline">{callBadge}</span> : undefined}
                badge={callEntry.visible ? undefined : contactButtonBadge}
                onMainClick={callEntry.visible ? callEntry.openModal : (event) => onViewContactDataPressed(event, true)}
                menuLabel={t('cv.detail.action.show-contact.label')}
                menuHeader={contactPriceElement}
                items={contactItems}
              />
            )}

            {/* Guest — single sign-in CTA that returns to this CV after login */}
            {!isAuth && !cvDetail?.isOwnCV && (
              <Button
                color="secondary"
                className={clsx(
                  'flex h-12 min-w-0 flex-1 items-center justify-center rounded-xl md:flex-none',
                  '!text-sm font-semibold text-white hover:!bg-secondary/90',
                )}
                onClick={() => redirectToLogin(typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : undefined)}
              >
                {t('cv.detail.action.signin_to_contact', 'Sign in to contact this user')}
              </Button>
            )}

            {/* More — download CV as default action, dropdown for the rest */}
            <CvActionButton
              variant="soft"
              label={canDownload
                ? t('global.action.download.label')
                : <span className="inline-block first-letter:uppercase">{t('global.btn.more')}</span>}
              subLabel={canDownload ? 'PDF' : undefined}
              icon={canDownload ? <ArrowDownTrayIcon /> : <EllipsisHorizontalIcon className="size-5" />}
              onMainClick={canDownload ? () => checkUnlockPdf() : undefined}
              collapseLabel
              menuLabel={t('global.btn.more')}
              items={moreItems}
            />
          </div>

        </div>,
        actionsContainer,
      )}
    </>
  );
};
